import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Sparkles, ShoppingCart, ChevronLeft, Users, Target,
  RefreshCw, CheckCircle2, ArrowRight
} from 'lucide-react'
import { api } from '@/lib/api'
import DayColumn from '@/components/DayColumn'
import { MacroBar } from '@/components/MacroBar'
import { sumWeekMacros, formatDate } from '@/lib/utils'
import type { FullWeeklyPlan, Dish } from '@/types/models'

interface Props {
  planId: string
  onBack: () => void
  onProceedToShopping: (planId: string) => void
}

export default function MealBuilder({ planId, onBack, onProceedToShopping }: Props) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [selectedDayIndex, setSelectedDayIndex] = useState(0)
  const [generatingAI, setGeneratingAI] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)

  const { data: planRes, isLoading } = useQuery({
    queryKey: ['plan', planId],
    queryFn: async () => {
      const token = await getToken()
      return api.get<{ data: FullWeeklyPlan }>(`/plans/${planId}`, token!)
    },
  })

  const plan = (planRes as { data: FullWeeklyPlan } | undefined)?.data
  const days = plan?.days ?? []

  const weekMacros = sumWeekMacros(days)

  // AI: Generate full menu
  async function handleGenerateMenu() {
    if (!plan) return
    setGeneratingAI(true)
    setAiError(null)

    try {
      const token = await getToken()

      // Fetch favorites for context
      const favsRes = await api.get<{ data: Dish[] }>('/dishes/favorites', token!)
      const favNames = ((favsRes as { data: Dish[] }).data ?? []).map(d => d.name)

      const res = await api.post<{
        data: {
          days: Array<{
            day_index: number
            meals: Array<{
              slot_index: number
              slot_type: string
              label: string
              dish_name: string
              calories_per_person: number
              protein_g: number
              carbs_g: number
              fat_g: number
              fiber_g: number
            }>
          }>
        }
      }>('/ai/generate-menu', {
        plan_id: plan.id,
        days_count: plan.days_count,
        meals_per_day: plan.default_meals_per_day,
        group_size: plan.default_group_size,
        diet_types: plan.diet_types,
        goal_mode: plan.goal_mode,
        calorie_target: plan.calorie_target,
        protein_target_g: plan.protein_target_g,
        favorites: favNames,
      }, token!)

      const menu = (res as { data: typeof res['data'] }).data

      // Apply each day's meals to existing slots
      const dayMap = Object.fromEntries(days.map(d => [d.day_index, d]))

      for (const aiDay of menu.days) {
        const day = dayMap[aiDay.day_index]
        if (!day) continue

        for (const aiMeal of aiDay.meals) {
          const existingSlot = day.slots.find(s => s.slot_index === aiMeal.slot_index)
          if (existingSlot) {
            await api.patch(`/slots/${existingSlot.id}`, {
              dish_name: aiMeal.dish_name,
              is_skipped: false,
              skip_reason: null,
            }, token!)
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: ['plan', planId] })
    } catch (err) {
      setAiError('AI menu generation failed. Please try again.')
    } finally {
      setGeneratingAI(false)
    }
  }

  // Count how many meals are filled
  const totalSlots = days.flatMap(d => d.slots).filter(s => s.slot_type === 'meal').length
  const filledSlots = days.flatMap(d => d.slots).filter(s => s.dish_name && !s.is_skipped).length
  const completionPct = totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0

  if (isLoading || !plan) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading your plan…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="btn-ghost p-2">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Week of {formatDate(plan.week_start_date)}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {plan.default_group_size} {plan.default_group_size === 1 ? 'person' : 'people'}
              </span>
              <span className="flex items-center gap-1">
                <Target className="w-3.5 h-3.5" />
                {plan.goal_mode === 'calories'
                  ? `${plan.calorie_target} kcal/day`
                  : `${plan.protein_target_g}g protein · ${plan.carbs_target_g}g carbs`}
              </span>
              {plan.diet_types.length > 0 && (
                <span className="text-gray-400">{plan.diet_types.join(', ')}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleGenerateMenu}
            disabled={generatingAI}
            className="btn-secondary flex items-center gap-2"
          >
            {generatingAI ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Generating…</>
            ) : (
              <><Sparkles className="w-4 h-4 text-brand-600" /> Make a Menu for Me</>
            )}
          </button>

          <button
            onClick={() => onProceedToShopping(plan.id)}
            className="btn-primary flex items-center gap-2"
          >
            <ShoppingCart className="w-4 h-4" />
            Shopping List
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {aiError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
          {aiError}
        </div>
      )}

      {/* Progress bar */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">
            Plan progress — {filledSlots} of {totalSlots} meals filled
          </p>
          <div className="flex items-center gap-1.5 text-sm">
            {completionPct === 100
              ? <><CheckCircle2 className="w-4 h-4 text-green-500" /><span className="text-green-600 font-medium">Complete</span></>
              : <span className="text-gray-500">{completionPct}%</span>
            }
          </div>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${completionPct === 100 ? 'bg-green-500' : 'bg-brand-500'}`}
            style={{ width: `${completionPct}%` }}
          />
        </div>
      </div>

      {/* Meal grid - horizontally scrollable */}
      <div className="overflow-x-auto pb-2 -mx-4 px-4">
        <div className="flex gap-3" style={{ minWidth: `${days.length * 212}px` }}>
          {days.map((day, i) => (
            <div key={day.id} className="flex-shrink-0 w-[200px]">
              <DayColumn
                day={day}
                plan={plan}
                isSelected={selectedDayIndex === i}
                onSelect={() => setSelectedDayIndex(i)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Weekly summary */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Weekly Summary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {plan.goal_mode === 'calories' ? (
            <>
              <MacroBar
                label="Total Calories"
                value={weekMacros.calories ?? 0}
                target={(plan.calorie_target ?? 0) * plan.default_group_size * plan.days_count}
                unit="kcal"
                color="bg-orange-400"
              />
              <MacroBar
                label="Avg / Day / Person"
                value={(weekMacros.calories ?? 0) / Math.max(1, days.filter(d => !d.is_eating_out).length) / plan.default_group_size}
                target={plan.calorie_target}
                unit="kcal"
                color="bg-orange-300"
              />
            </>
          ) : (
            <>
              <MacroBar label="Protein" value={weekMacros.protein_g ?? 0} target={(plan.protein_target_g ?? 0) * plan.default_group_size * plan.days_count} color="bg-blue-400" />
              <MacroBar label="Carbs" value={weekMacros.carbs_g ?? 0} target={(plan.carbs_target_g ?? 0) * plan.default_group_size * plan.days_count} color="bg-yellow-400" />
              <MacroBar label="Fat" value={weekMacros.fat_g ?? 0} target={(plan.fat_target_g ?? 0) * plan.default_group_size * plan.days_count} color="bg-purple-400" />
              <MacroBar label="Fiber" value={weekMacros.fiber_g ?? 0} target={(plan.fiber_target_g ?? 0) * plan.default_group_size * plan.days_count} color="bg-green-400" />
            </>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400 mb-2">This week at a glance</p>
          <div className="flex flex-wrap gap-1.5">
            {days.flatMap(d => d.slots).filter(s => s.dish_name && !s.is_skipped).slice(0, 12).map(s => (
              <span key={s.id} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                {s.dish_name}
              </span>
            ))}
            {filledSlots > 12 && (
              <span className="text-xs text-gray-400 px-2 py-0.5">+{filledSlots - 12} more</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
